import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Trash2, Tag, Plus } from "lucide-react";

const SERVICES = [
  { key: "labour_registry", label: "Labour Registry" },
  { key: "callout_fee", label: "Callout Fee" },
];

const emptyForm = {
  code: "",
  discount_type: "percent",
  discount_value: "",
  labour_registry: true,
  callout_fee: false,
  start_date: "",
  end_date: "",
  description: "",
  max_redemptions: "",
};

export const PromoCodesTab = ({ API }) => {
  const [promos, setPromos] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [creating, setCreating] = useState(false);

  const fetchPromos = async () => {
    try {
      const { data } = await axios.get(`${API}/admin/promo-codes`);
      setPromos(data);
    } catch (e) {
      /* silent */
    }
  };

  useEffect(() => {
    fetchPromos();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const createPromo = async () => {
    const applies_to = SERVICES.filter((s) => form[s.key]).map((s) => s.key);
    if (!form.code.trim()) return toast.error("Enter a code");
    if (!form.discount_value || parseFloat(form.discount_value) <= 0)
      return toast.error("Enter a discount value");
    if (applies_to.length === 0) return toast.error("Select at least one service");
    setCreating(true);
    try {
      await axios.post(`${API}/admin/promo-codes`, {
        code: form.code.trim(),
        discount_type: form.discount_type,
        discount_value: parseFloat(form.discount_value),
        applies_to,
        start_date: form.start_date || null,
        end_date: form.end_date || null,
        description: form.description || null,
        max_redemptions: form.max_redemptions ? parseInt(form.max_redemptions) : null,
        active: true,
      });
      toast.success("Promo code created");
      setForm(emptyForm);
      fetchPromos();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to create promo code");
    } finally {
      setCreating(false);
    }
  };

  const toggleActive = async (promo) => {
    try {
      await axios.patch(`${API}/admin/promo-codes/${promo.id}`, { active: !promo.active });
      setPromos((prev) => prev.map((p) => (p.id === promo.id ? { ...p, active: !p.active } : p)));
    } catch (e) {
      toast.error("Failed to update promo code");
    }
  };

  const deletePromo = async (promo) => {
    if (!window.confirm(`Delete promo code ${promo.code}?`)) return;
    try {
      await axios.delete(`${API}/admin/promo-codes/${promo.id}`);
      setPromos((prev) => prev.filter((p) => p.id !== promo.id));
      toast.success("Promo code deleted");
    } catch (e) {
      toast.error("Failed to delete promo code");
    }
  };

  return (
    <div className="grid lg:grid-cols-3 gap-6" data-testid="promo-codes-tab">
      {/* Create form */}
      <Card className="bg-white lg:col-span-1 h-fit">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <Tag className="w-5 h-5 text-orange-500" /> New Promo Code
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label>Code *</Label>
            <Input
              value={form.code}
              onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })}
              placeholder="e.g. LAUNCH20"
              className="mt-1 uppercase"
              data-testid="promo-code-input"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Type *</Label>
              <Select value={form.discount_type} onValueChange={(v) => setForm({ ...form, discount_type: v })}>
                <SelectTrigger className="mt-1" data-testid="promo-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="percent">Percentage (%)</SelectItem>
                  <SelectItem value="fixed">Fixed (XCD $)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Value *</Label>
              <Input
                type="number" min="0" step="0.01"
                value={form.discount_value}
                onChange={(e) => setForm({ ...form, discount_value: e.target.value })}
                placeholder={form.discount_type === "percent" ? "20" : "50"}
                className="mt-1"
                data-testid="promo-value-input"
              />
            </div>
          </div>
          <div>
            <Label className="mb-2 block">Applies to *</Label>
            <div className="space-y-2">
              {SERVICES.map((s) => (
                <label key={s.key} className="flex items-center gap-2 cursor-pointer text-sm">
                  <input
                    type="checkbox"
                    checked={form[s.key]}
                    onChange={(e) => setForm({ ...form, [s.key]: e.target.checked })}
                    className="w-4 h-4 accent-orange-500"
                    data-testid={`promo-service-${s.key}`}
                  />
                  {s.label}
                </label>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Start date</Label>
              <Input type="date" value={form.start_date}
                onChange={(e) => setForm({ ...form, start_date: e.target.value })}
                className="mt-1" data-testid="promo-start-date" />
            </div>
            <div>
              <Label>End date</Label>
              <Input type="date" value={form.end_date}
                onChange={(e) => setForm({ ...form, end_date: e.target.value })}
                className="mt-1" data-testid="promo-end-date" />
            </div>
          </div>
          <div>
            <Label>Description</Label>
            <Input value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              placeholder="Shown at checkout (optional)" className="mt-1" data-testid="promo-description" />
          </div>
          <div>
            <Label>Max redemptions</Label>
            <Input type="number" min="1" value={form.max_redemptions}
              onChange={(e) => setForm({ ...form, max_redemptions: e.target.value })}
              placeholder="Unlimited (e.g. 50)" className="mt-1" data-testid="promo-max-redemptions" />
          </div>
          <Button
            onClick={createPromo}
            disabled={creating}
            className="w-full bg-orange-500 hover:bg-orange-600 text-white"
            data-testid="promo-create-btn"
          >
            <Plus className="w-4 h-4 mr-1" /> {creating ? "Creating..." : "Create Promo Code"}
          </Button>
        </CardContent>
      </Card>

      {/* List */}
      <Card className="bg-white lg:col-span-2">
        <CardHeader>
          <CardTitle className="text-lg">Active & Past Codes</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {promos.map((p) => (
              <div
                key={p.id}
                className="flex items-center justify-between gap-4 p-4 rounded-lg border border-slate-200"
                data-testid={`promo-row-${p.code}`}
              >
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-bold text-slate-900 tracking-wide">{p.code}</span>
                    <Badge className="bg-orange-100 text-orange-700">
                      {p.discount_type === "percent" ? `${p.discount_value}% off` : `XCD $${p.discount_value} off`}
                    </Badge>
                    {p.active ? (
                      <Badge className="bg-green-100 text-green-700">Active</Badge>
                    ) : (
                      <Badge className="bg-slate-100 text-slate-500">Inactive</Badge>
                    )}
                  </div>
                  <p className="text-xs text-slate-500 mt-1">
                    {(p.applies_to || []).map((s) => SERVICES.find((x) => x.key === s)?.label || s).join(", ")}
                    {(p.start_date || p.end_date) && (
                      <> · {p.start_date || "…"} → {p.end_date || "…"}</>
                    )}
                    <> · {p.times_used || 0}{p.max_redemptions ? ` / ${p.max_redemptions}` : ""} used</>
                  </p>
                </div>
                <div className="flex items-center gap-3 flex-shrink-0">
                  <Switch
                    checked={p.active}
                    onCheckedChange={() => toggleActive(p)}
                    data-testid={`promo-toggle-${p.code}`}
                  />
                  <Button
                    variant="ghost" size="sm"
                    onClick={() => deletePromo(p)}
                    className="text-red-600 hover:text-red-700 hover:bg-red-50"
                    data-testid={`promo-delete-${p.code}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            ))}
            {promos.length === 0 && (
              <p className="text-center py-8 text-slate-500">No promo codes yet. Create one on the left.</p>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default PromoCodesTab;
